import { Component, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormBuilder, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { IonicModule } from '@ionic/angular';
import { ACCOUNT_DEACTIVATED, AuthService } from 'src/app/services/authentication/auth.service';
import { AuthBgComponent } from '../auth/ui/auth-bg.component';

@Component({
  selector: 'app-login',
  templateUrl: './login.page.html',
  styleUrls: ['./login.page.scss'],
  standalone: true,
  imports: [CommonModule, ReactiveFormsModule, IonicModule, AuthBgComponent]
})
export class LoginPage {
  // ============ Dependency Injection ============
  private auth = inject(AuthService);
  private router = inject(Router);
  private fb = inject(FormBuilder);

  // ============ Component State ============
  credentials: FormGroup = this.fb.group({
    email: ['', [Validators.required, Validators.email]],
    password: ['', [Validators.required, Validators.minLength(6)]]
  });
  showPwd = false;
  submitting = false;
  /** Inline error banner state (no alert dialogs). */
  errorState: 'wrongPassword' | null = null;
  /** When true, render the "Account paused" dead-end instead of the form. */
  deactivated = false;
  deactivatedEmail = '';
  // TODO(Phase 3/config): source the admin contact from app settings instead of a constant.
  readonly adminEmail = 'admin@procap.co';

  get email() {
    return this.credentials.get('email');
  }
  get password() {
    return this.credentials.get('password');
  }

  /** Clear the inline error as soon as the user edits the password. */
  onPasswordInput() {
    if (this.errorState) {
      this.errorState = null;
    }
  }

  async login() {
    if (this.credentials.invalid || this.submitting) {
      this.credentials.markAllAsTouched();
      return;
    }

    this.submitting = true;
    this.errorState = null;
    const email = this.credentials.value.email.trim();

    try {
      const user = await this.auth.login(email, this.credentials.value.password);
      if (user) {
        const isClient = await this.auth.isUserClient(user.uid);
        this.router.navigateByUrl(isClient ? '/private/tabs/landing' : '/private/home', { replaceUrl: true });
      } else {
        this.errorState = 'wrongPassword';
      }
    } catch (error: unknown) {
      if (error instanceof Error && error.message === ACCOUNT_DEACTIVATED) {
        this.deactivatedEmail = email;
        this.deactivated = true;
      } else {
        this.errorState = 'wrongPassword';
      }
    } finally {
      this.submitting = false;
    }
  }

  /** Forgot password → styled recovery flow, passing the typed email through. */
  onForgot() {
    const email = (this.credentials.value.email || '').trim();
    this.router.navigateByUrl('/forgot', { state: { email } });
  }

  /** Footer link → claim flow (currently the register route; becomes `/claim` in Phase 3). */
  onClaim() {
    this.router.navigateByUrl('/register');
  }

  /** Deactivated screen → open the user's mail client to the admin. */
  emailAdministrator() {
    window.open(`mailto:${this.adminEmail}`, '_system');
  }

  /** Deactivated screen → return to a clean sign-in form. */
  signInAsDifferent() {
    this.deactivated = false;
    this.deactivatedEmail = '';
    this.errorState = null;
    this.credentials.reset({ email: '', password: '' });
  }
}
